import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { listarSesiones } from "../../api";
import "./AdminHome.css";

export default function AdminHome() {
  const [citas, setCitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState(false);
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");

  // Avisos / recordatorios de mañana
  const [avisos, setAvisos] = useState([]);
  const [loadingAvisos, setLoadingAvisos] = useState(true);

  // Cumpleaños de hoy
  const [cumplesHoy, setCumplesHoy] = useState([]);
  const [loadingCumples, setLoadingCumples] = useState(true);

  // Lista de espera
  const [listaEspera, setListaEspera] = useState([]);
  const [loadingListaEspera, setLoadingListaEspera] = useState(true);
  const [updatingWaitlistId, setUpdatingWaitlistId] = useState(null);
  const [resumenListaEspera, setResumenListaEspera] = useState({
    pending: 0,
    contacted: 0,
    booked: 0,
    cancelled: 0,
    pending_today: 0,
    pending_future: 0,
  });

  const navigate = useNavigate();

  const extractPatientId = (cita) => {
    return (
      cita.patient_id ??
      cita.paciente_id ??
      cita.patientId ??
      cita.pacienteId ??
      cita.user_id ??
      cita.client_id ??
      cita.child_id ??
      cita.patient?.id ??
      cita.paciente?.id ??
      cita.user?.id ??
      cita.client?.id ??
      null
    );
  };

  const goToPacienteView = useCallback(
    (cita) => {
      const patientId = extractPatientId(cita);

      if (!patientId) {
        console.warn("[AdminHome] No se encontró patient_id en la cita:", cita);
        alert(
          "No se pudo abrir la ficha porque la cita no trae el ID del paciente.\n\n" +
            "Asegúrate de que listarSesiones incluya 'patient_id' o 'patient.id' en cada cita."
        );
        return;
      }

      navigate(`/paciente/${patientId}`);
    },
    [navigate]
  );

  useEffect(() => {
    const checkSize = () => setIsMobileOrTablet(window.innerWidth <= 1024);
    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);

  useEffect(() => {
    const hoy = new Date().toISOString().split("T")[0];
    listarSesiones({ date: hoy })
      .then((res) => {
        if (res?.ok) setCitas(res.items || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const cargarAvisosManiana = useCallback(async () => {
    setLoadingAvisos(true);
    try {
      const res = await fetch(
        "https://te-ayudo.es/back/api/get_tomorrow_reminders.php"
      );
      const data = await res.json();

      if (data.ok) {
        setAvisos(data.items || []);
      } else {
        console.error("Error get_tomorrow_reminders:", data.error);
        setAvisos([]);
      }
    } catch (err) {
      console.error("Error cargando avisos de mañana:", err);
      setAvisos([]);
    } finally {
      setLoadingAvisos(false);
    }
  }, []);

  const cargarCumplesHoy = useCallback(async () => {
    setLoadingCumples(true);

    try {
      const res = await fetch(
        "https://te-ayudo.es/back/api/get_birthdays_today.php"
      );
      const data = await res.json();

      if (data.ok) {
        const items = (data.items || []).filter((p) => {
          const edad = Number(p.edad) || 0;
          return edad >= 3;
        });

        setCumplesHoy(items);
      } else {
        console.error("Error get_birthdays_today:", data.error);
        setCumplesHoy([]);
      }
    } catch (err) {
      console.error("Error cargando cumpleaños:", err);
      setCumplesHoy([]);
    } finally {
      setLoadingCumples(false);
    }
  }, []);

  const cargarListaEspera = useCallback(async () => {
    setLoadingListaEspera(true);

    try {
      const res = await fetch(
        "https://te-ayudo.es/back/api/waitlist.list.php?status=pending&page_size=100",
        {
          credentials: "include",
        }
      );

      const data = await res.json();

      if (data.ok) {
        setListaEspera(data.items || []);
        setResumenListaEspera(
          data.summary || {
            pending: 0,
            contacted: 0,
            booked: 0,
            cancelled: 0,
            pending_today: 0,
            pending_future: 0,
          }
        );
      } else {
        console.error("Error waitlist.list:", data.error);
        setListaEspera([]);
      }
    } catch (err) {
      console.error("Error cargando lista de espera:", err);
      setListaEspera([]);
    } finally {
      setLoadingListaEspera(false);
    }
  }, []);

  useEffect(() => {
    cargarAvisosManiana();
    cargarCumplesHoy();
    cargarListaEspera();
  }, [cargarAvisosManiana, cargarCumplesHoy, cargarListaEspera]);

  const filtradas = citas.filter((c) =>
    (c.paciente || "").toLowerCase().includes(busqueda.toLowerCase())
  );

  const nextCard = () => {
    if (currentIndex < filtradas.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const prevCard = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const handleSendReminders = async () => {
    setSending(true);
    setMsg("Enviando recordatorios...");

    try {
      const res = await fetch(
        "https://te-ayudo.es/back/api/manual_send_reminders.php?key=mi_token_seguro_2025"
      );
      const data = await res.json();

      if (data.ok) {
        const enviadosAhora = (data.reminders || []).filter(
          (a) => a.estado === "enviado"
        );
        const numEnviados = enviadosAhora.length;

        setMsg(
          `✅ Recordatorios enviados correctamente${
            numEnviados ? ` (${numEnviados} enviados).` : "."
          }`
        );

        await cargarAvisosManiana();
      } else {
        setMsg(
          `❌ Error: ${data.error || "No se pudieron enviar los recordatorios"}`
        );
      }
    } catch (err) {
      console.error(err);
      setMsg("⚠️ Error de conexión con el servidor.");
    } finally {
      setSending(false);
      setTimeout(() => setMsg(""), 5000);
    }
  };

  const actualizarEstadoListaEspera = async (
    item,
    status
  ) => {
    const labels = {
      contacted: "marcar como contactado",
      booked: "marcar como citado",
      cancelled: "cerrar",
    };

    const actionLabel = labels[status] || "actualizar";

    if (
      !window.confirm(
        `¿Quieres ${actionLabel} a ${item.patient_name || "este paciente"}?`
      )
    ) {
      return;
    }

    setUpdatingWaitlistId(item.id);

    try {
      const res = await fetch(
        "https://te-ayudo.es/back/api/waitlist.update.php",
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: item.id,
            status,
          }),
        }
      );

      const data = await res.json();

      if (!res.ok || !data.ok) {
        throw new Error(
          data.error ||
            "No se ha podido actualizar la lista de espera."
        );
      }

      await cargarListaEspera();
    } catch (err) {
      console.error("Error actualizando lista de espera:", err);

      alert(
        err.message ||
          "No se ha podido actualizar la lista de espera."
      );
    } finally {
      setUpdatingWaitlistId(null);
    }
  };

  const formatWaitlistDate = (value) => {
    if (!value) return "-";

    const date = new Date(`${value}T12:00:00`);

    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatSpecialty = (value) => {
    const normalized = String(value || "").toLowerCase();

    if (normalized === "psicologia") return "Psicología";
    if (normalized === "logopedia") return "Logopedia";
    if (normalized === "nutricion") return "Nutrición";

    return value || "-";
  };

  return (
    <div className="pl-wrap">
      <section className="mb-8">
        <div className="flex items-center justify-between gap-4 mb-3">
          <div>
            <h2 className="text-3xl font-bold text-[#704c3b]">
              Lista de espera
            </h2>

            <p className="text-[#7a6a5c] mt-1">
              Personas pendientes de contactar si se libera un hueco.
            </p>
          </div>

          <div className="px-4 py-2 rounded-xl bg-[#f6f3ec] text-[#704c3b] font-semibold whitespace-nowrap">
            {resumenListaEspera.pending || 0} pendientes
          </div>
        </div>

        {loadingListaEspera ? (
          <div className="pl-empty">Cargando lista de espera...</div>
        ) : listaEspera.length === 0 ? (
          <div className="pl-empty">
            No hay personas pendientes en lista de espera.
          </div>
        ) : (
          <div className="pl-table-scroll">
            <table className="pl-table">
              <thead>
                <tr>
                  <th>Paciente</th>
                  <th>Especialidad</th>
                  <th>Disponibilidad</th>
                  <th>Modalidad</th>
                  <th>Tipo</th>
                  <th>Teléfono</th>
                  <th>Observaciones</th>
                  <th>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {listaEspera.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{item.patient_name || "-"}</strong>
                      {item.tutor_name ? (
                        <div className="text-xs text-gray-500 mt-1">
                          Tutor: {item.tutor_name}
                        </div>
                      ) : null}
                    </td>

                    <td>
                      {formatSpecialty(item.specialty_code)}
                    </td>

                    <td>
                      {item.alternative_dates
                        ? "Cualquier otro día"
                        : formatWaitlistDate(item.requested_date)}
                    </td>

                    <td className="capitalize">
                      {item.modality || "-"}
                    </td>

                    <td>
                      {item.first_visit
                        ? "Primera visita"
                        : "Paciente actual"}
                    </td>

                    <td>
                      {item.tutor_phone ? (
                        <a href={`tel:${item.tutor_phone}`}>
                          {item.tutor_phone}
                        </a>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td>
                      {item.notes || "-"}
                    </td>

                    <td>
                      <div className="waitlist-actions">
                        <button
                          type="button"
                          className="waitlist-action waitlist-action-contacted"
                          disabled={updatingWaitlistId === item.id}
                          onClick={() =>
                            actualizarEstadoListaEspera(
                              item,
                              "contacted"
                            )
                          }
                        >
                          Contactado
                        </button>

                        <button
                          type="button"
                          className="waitlist-action waitlist-action-booked"
                          disabled={updatingWaitlistId === item.id}
                          onClick={() =>
                            actualizarEstadoListaEspera(
                              item,
                              "booked"
                            )
                          }
                        >
                          Citado
                        </button>

                        <button
                          type="button"
                          className="waitlist-action waitlist-action-close"
                          disabled={updatingWaitlistId === item.id}
                          onClick={() =>
                            actualizarEstadoListaEspera(
                              item,
                              "cancelled"
                            )
                          }
                        >
                          Cerrar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <h2 className="text-3xl font-bold mb-2 text-[#704c3b]">
        📅 Citas del día
      </h2>

      <p className="text-[#7a6a5c] mb-6">
        Aquí puedes ver las sesiones programadas para <strong>hoy</strong>, los
        recordatorios de <strong>mañana</strong> y los{" "}
        <strong>cumpleaños del día</strong>.
      </p>

      <div className="mb-6">
        <button
          onClick={handleSendReminders}
          disabled={sending}
          className="px-4 py-2 bg-[#704c3b] text-white rounded-lg hover:bg-[#8a6550] transition disabled:opacity-60"
        >
          {sending ? "Enviando..." : "🔔 Enviar recordatorios manualmente"}
        </button>

        {msg && <p className="mt-2 text-sm text-gray-700">{msg}</p>}

        <div className="mt-4">
          <h3 className="font-semibold text-[#704c3b] mb-2">
            Recordatorios para mañana
          </h3>

          {loadingAvisos ? (
            <p className="text-sm text-gray-600">
              Cargando citas de mañana...
            </p>
          ) : avisos.length === 0 ? (
            <p className="text-sm text-gray-600">
              No hay citas programadas para mañana.
            </p>
          ) : (
            <div className="pl-table-scroll">
              <table className="pl-table">
                <thead>
                  <tr>
                    <th>Paciente</th>
                    <th>Tutor</th>
                    <th>Email tutor</th>
                    <th>Teléfono</th>
                    <th>Fecha</th>
                    <th>Hora</th>
                    <th>Servicio</th>
                    <th>Estado recordatorio</th>
                  </tr>
                </thead>
                <tbody>
                  {avisos.map((aviso) => (
                    <tr key={aviso.session_id}>
                      <td>{aviso.paciente}</td>
                      <td>{aviso.tutor_nombre || "-"}</td>
                      <td>{aviso.tutor_email || "-"}</td>
                      <td>{aviso.tutor_telefono || "-"}</td>
                      <td>{aviso.fecha}</td>
                      <td>{aviso.hora}</td>
                      <td>{aviso.service_code || "-"}</td>
                      <td
                        className={`capitalize ${
                          aviso.estado === "enviado"
                            ? "text-green-600"
                            : "text-orange-600"
                        }`}
                      >
                        {aviso.estado || "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6">
          <h3 className="font-semibold text-[#704c3b] mb-2">
            🎂 Cumpleaños de hoy
          </h3>

          {loadingCumples ? (
            <p className="text-sm text-gray-600">Cargando cumpleaños...</p>
          ) : cumplesHoy.length === 0 ? (
            <p className="text-sm text-gray-600">
              No hay pacientes que cumplan años hoy (3+ años).
            </p>
          ) : (
            <>
              <p className="text-sm text-[#704c3b] mb-2">
                Hoy cumplen años <strong>{cumplesHoy.length}</strong>{" "}
                paciente{cumplesHoy.length !== 1 ? "s" : ""}.
              </p>

              <div className="pl-table-scroll">
                <table className="pl-table">
                  <thead>
                    <tr>
                      <th>Paciente</th>
                      <th>Edad que cumple</th>
                      <th>Tutor</th>
                      <th>Teléfono</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cumplesHoy.map((p) => (
                      <tr key={p.id || p.patient_id}>
                        <td>{p.nombre_completo || p.paciente || "-"}</td>
                        <td>{p.edad ? `${p.edad} años` : "-"}</td>
                        <td>{p.tutor_nombre || "-"}</td>
                        <td>{p.tutor_telefono || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="pl-tools">
        <input
          type="search"
          placeholder="Buscar paciente..."
          value={busqueda}
          onChange={(e) => {
            setBusqueda(e.target.value);
            setCurrentIndex(0);
          }}
        />
        <div className="pl-count">{filtradas.length} citas hoy</div>
      </div>

      <div
        className={`pl-table-scroll ${isMobileOrTablet ? "mobile-clean" : ""}`}
      >
        {loading ? (
          <div className="pl-empty">Cargando citas...</div>
        ) : filtradas.length === 0 ? (
          <div className="pl-empty">No hay citas programadas para hoy.</div>
        ) : (
          <>
            {!isMobileOrTablet && (
              <table className="pl-table">
                <thead>
                  <tr>
                    <th>Paciente</th>
                    <th>Hora</th>
                    <th>Servicio</th>
                    <th>Especialidad</th>
                  </tr>
                </thead>
                <tbody>
                  {filtradas.map((cita) => {
                    const hora = new Date(cita.start_datetime).toLocaleTimeString(
                      "es-ES",
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    );

                    return (
                      <tr
                        key={cita.id}
                        className="clickable-row"
                        title="Ver ficha del paciente"
                        onClick={() => goToPacienteView(cita)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            goToPacienteView(cita);
                          }
                        }}
                      >
                        <td>{cita.paciente}</td>
                        <td>{hora}</td>
                        <td className="capitalize">
                          {cita.service_code?.replace(/_/g, " ") || "-"}
                        </td>
                        <td className="capitalize">{cita.specialty || "-"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}

            {isMobileOrTablet && (
              <div className="mobile-carrusel">
                {filtradas.length > 1 && (
                  <button
                    className="arrow left"
                    onClick={prevCard}
                    disabled={currentIndex === 0}
                  >
                    ◀
                  </button>
                )}

                <div className="cards-container">
                  {filtradas.length > 0 && (
                    <div
                      key={filtradas[currentIndex].id}
                      className="card-cita clickable-card"
                      title="Ver ficha del paciente"
                      onClick={() => goToPacienteView(filtradas[currentIndex])}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          goToPacienteView(filtradas[currentIndex]);
                        }
                      }}
                    >
                      <h3>{filtradas[currentIndex].paciente}</h3>
                      <p>
                        <strong>Hora:</strong>{" "}
                        {new Date(
                          filtradas[currentIndex].start_datetime
                        ).toLocaleTimeString("es-ES", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                      <p>
                        <strong>Servicio:</strong>{" "}
                        {filtradas[currentIndex].service_code?.replace(/_/g, " ") ||
                          "-"}
                      </p>
                      <p>
                        <strong>Especialidad:</strong>{" "}
                        {filtradas[currentIndex].specialty || "-"}
                      </p>
                    </div>
                  )}
                </div>

                {filtradas.length > 1 && (
                  <button
                    className="arrow right"
                    onClick={nextCard}
                    disabled={currentIndex === filtradas.length - 1}
                  >
                    ▶
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}